import React, { createContext, useContext, useEffect, useCallback, useState, useRef, ReactNode } from "react";
import { useSocket } from "../hooks/useSocket";

export interface UserStatus {
  isOnline: boolean;
  lastSeenAt: string | null;
}

interface OnlineStatusContextType {
  getStatus: (userId: string) => UserStatus;
  /** Returns true if we have received status data for this user (via fetch or socket event) */
  hasStatus: (userId: string) => boolean;
  fetchStatuses: (userIds: string[]) => Promise<void>;
  /** Subscribe to status changes for a specific user. Returns an unsubscribe function. */
  subscribeUser: (userId: string, callback: () => void) => () => void;
}

interface UserStatusChangedPayload {
  userId: string;
  status: "online" | "offline";
  lastSeenAt: string | null;
}

interface BulkStatusDTO {
  userId: string;
  isOnline: boolean;
  lastSeenAt: string | null;
}

export const DEFAULT_STATUS: UserStatus = { isOnline: false, lastSeenAt: null };

const OnlineStatusContext = createContext<OnlineStatusContextType | undefined>(undefined);

export const OnlineStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { subscribe } = useSocket();
  const statusMapRef = useRef<Map<string, UserStatus>>(new Map());
  const subscribersRef = useRef<Map<string, Set<() => void>>>(new Map());
  const [, setVersion] = useState(0);

  const notifySubscribers = useCallback((userId: string) => {
    const subs = subscribersRef.current.get(userId);
    if (subs) {
      subs.forEach((cb) => cb());
    }
  }, []);

  // Subscribe to real-time presence events from both event names
  // (backend emits user_status_changed; design also references presence.updated)
  useEffect(() => {
    const handler = (payload: UserStatusChangedPayload) => {
      const { userId, status, lastSeenAt } = payload;
      statusMapRef.current.set(userId, {
        isOnline: status === "online",
        lastSeenAt,
      });
      notifySubscribers(userId);
      setVersion((v) => v + 1);
    };

    const unsub1 = subscribe("user_status_changed", handler);
    const unsub2 = subscribe("presence.updated", handler);

    return () => {
      unsub1();
      unsub2();
    };
  }, [subscribe, notifySubscribers]);

  const getStatus = useCallback((userId: string): UserStatus => {
    return statusMapRef.current.get(userId) ?? DEFAULT_STATUS;
  }, []);

  const hasStatus = useCallback((userId: string): boolean => {
    return statusMapRef.current.has(userId);
  }, []);

  const subscribeUser = useCallback((userId: string, callback: () => void): () => void => {
    let subs = subscribersRef.current.get(userId);
    if (!subs) {
      subs = new Set();
      subscribersRef.current.set(userId, subs);
    }
    subs.add(callback);

    return () => {
      const currentSubs = subscribersRef.current.get(userId);
      if (currentSubs) {
        currentSubs.delete(callback);
        if (currentSubs.size === 0) {
          subscribersRef.current.delete(userId);
        }
      }
    };
  }, []);

  const fetchBatch = useCallback(async (batchIds: string[]): Promise<BulkStatusDTO[]> => {
    const query = batchIds.join(",");
    const res = await fetch(`/api/users/status?userIds=${encodeURIComponent(query)}`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) {
      throw new Error(`Status fetch failed: ${res.status}`);
    }

    return res.json();
  }, []);

  const fetchStatuses = useCallback(async (userIds: string[]): Promise<void> => {
    // Backend accepts max 50 per request; cap input at 100 per call
    const ids = userIds.slice(0, 100);
    if (ids.length === 0) return;

    const batches: string[][] = [];
    for (let i = 0; i < ids.length; i += 50) {
      batches.push(ids.slice(i, i + 50));
    }

    const results = await Promise.allSettled(
      batches.map((batch) => fetchBatch(batch))
    );

    let updated = false;

    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      if (result.status === "fulfilled") {
        for (const entry of result.value) {
          statusMapRef.current.set(entry.userId, {
            isOnline: entry.isOnline,
            lastSeenAt: entry.lastSeenAt,
          });
          notifySubscribers(entry.userId);
        }
        updated = true;
      } else {
        // Graceful degradation: retain cached data, default uncached to offline
        for (const id of batches[i]) {
          if (!statusMapRef.current.has(id)) {
            statusMapRef.current.set(id, { isOnline: false, lastSeenAt: null });
            notifySubscribers(id);
          }
        }
        updated = true;
      }
    }

    if (updated) {
      setVersion((v) => v + 1);
    }
  }, [fetchBatch, notifySubscribers]);

  return (
    <OnlineStatusContext.Provider value={{ getStatus, hasStatus, fetchStatuses, subscribeUser }}>
      {children}
    </OnlineStatusContext.Provider>
  );
};

const FALLBACK_CONTEXT: OnlineStatusContextType = {
  getStatus: () => DEFAULT_STATUS,
  hasStatus: () => false,
  fetchStatuses: async () => {},
  subscribeUser: () => () => {},
};

export const useOnlineStatusContext = () => {
  const ctx = useContext(OnlineStatusContext);
  // Graceful fallback: return safe defaults when provider is missing.
  // This supports rendering in tests or isolated component previews.
  return ctx ?? FALLBACK_CONTEXT;
};
